import { AxiosRequestConfig } from "axios";
import { pigspinBackofficeApiInstance } from "../../utils/api/AxiosInstance";
import { fetchWithJSON } from "../../utils/api/fetch";
import { BaseRequest, BaseResponse2 } from "../service.type";

// NOTE : CORE has deployed their audit fields. `created_at` is the
// timestamp of the blacklist entry (naive UTC, converted to Asia/Bangkok
// for display), and `actor_name` / `actor_uid` identify who created it.
//
// `reason` ("เหตุผล") is free text captured at create time. It is nullable
// on purpose: CORE accepts a create without it, and every row added before
// CORE shipped the field comes back with `reason: null`. Render "-" rather
// than assuming a value is always there.
export type BlacklistItem = {
  uid: string;
  phone: string | null;
  bank_account_name: string | null;
  reason: string | null;
  created_at: string | null;
  actor_name: string | null;
  actor_uid: string | null;
};

export type BlacklistListResponse = BaseResponse2<{
  total?: number;
  page?: number;
  per_page?: number;
  total_pages?: number;
  results: BlacklistItem[];
}>;

export type BlacklistMutationResponse = BaseResponse2<BlacklistItem | {}>;

// Server-side paging. All optional: without them the backend returns its defaults.
export type BlacklistListParams = {
  page?: number;
  per_page?: number;
  search?: string;
};

export type GetBlacklistPhonesRequest = BaseRequest & BlacklistListParams;

const toListParams = ({ page, per_page, search }: BlacklistListParams) => ({
  page,
  per_page,
  search: search || undefined, // omit empty search
});

export const GetBlacklistPhones = async (
  request: GetBlacklistPhonesRequest
): Promise<BlacklistListResponse> => {
  const requestConfig: AxiosRequestConfig = {
    url: "v1/blacklist/phones",
    method: "GET",
    params: toListParams(request),
    headers: { token: request.token },
  };
  return fetchWithJSON<BlacklistListResponse>(pigspinBackofficeApiInstance, requestConfig);
};

export type CreateBlacklistPhoneRequest = BaseRequest & {
  phone: string;
  reason?: string;
};

export const CreateBlacklistPhone = async (
  request: CreateBlacklistPhoneRequest
): Promise<BlacklistMutationResponse> => {
  const { token, ...data } = request;
  const requestConfig: AxiosRequestConfig = {
    url: "v1/blacklist/phones",
    method: "POST",
    data,
    headers: { token },
  };
  return fetchWithJSON<BlacklistMutationResponse>(pigspinBackofficeApiInstance, requestConfig);
};

export type GetBlacklistBankAccountsRequest = BaseRequest & BlacklistListParams;

export const GetBlacklistBankAccounts = async (
  request: GetBlacklistBankAccountsRequest
): Promise<BlacklistListResponse> => {
  const requestConfig: AxiosRequestConfig = {
    url: "v1/blacklist/bank-accounts",
    method: "GET",
    params: toListParams(request),
    headers: { token: request.token },
  };
  return fetchWithJSON<BlacklistListResponse>(pigspinBackofficeApiInstance, requestConfig);
};

export type CreateBlacklistBankAccountRequest = BaseRequest & {
  bank_account_name: string;
  reason?: string;
};

export const CreateBlacklistBankAccount = async (
  request: CreateBlacklistBankAccountRequest
): Promise<BlacklistMutationResponse> => {
  const { token, ...data } = request;
  const requestConfig: AxiosRequestConfig = {
    url: "v1/blacklist/bank-accounts",
    method: "POST",
    data,
    headers: { token },
  };
  return fetchWithJSON<BlacklistMutationResponse>(pigspinBackofficeApiInstance, requestConfig);
};
